// src/App.js
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";

import Landing from "./Landing";
import StudentLogin from "./StudentLogin";
import StaffLogin from "./StaffLogin";
import DriverLogin from "./DriverLogin";
import AdminLogin from "./AdminLogin";

import DriverDashboard from "./DriverDashboard";
import TrackBus from "./TrackBus";
import HomePage from "./HomePage";

import AdminRouteUpdate from "./AdminRouteUpdate";
import AdminStudents from "./AdminStudents";

// Bus pages
import Bus1 from "./Bus1";
import Bus2 from "./Bus2";
import Bus3 from "./Bus3";
import Bus4 from "./Bus4";
import Bus5 from "./Bus5";
import Bus6 from "./Bus6";
import Bus7 from "./Bus7";
import Bus8 from "./Bus8";
import Bus9 from "./Bus9";
import Bus10 from "./Bus10";
import Bus11 from "./Bus11";
import Bus12 from "./Bus12";
import Bus13 from "./Bus13";
import Bus14 from "./Bus14";
import Bus15 from "./Bus15";

function RequireAuth({ user, children }) {
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function RequireDriver({ user, isDriver, children }) {
  if (!user) return <Navigate to="/" replace />;
  if (!isDriver) return <Navigate to="/" replace />;
  return children;
}

function RequireAdmin({ user, isAdmin, children }) {
  if (!user) return <Navigate to="/" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function Root({ user, isDriver, isAdmin }) {
  if (!user) return <Landing />;
  if (isAdmin) return <Navigate to="/admin" replace />;
  if (isDriver) return <Navigate to="/driver" replace />;
  return <HomePage />;
}

export default function App() {
  const [user, setUser] = useState(null);
  const [isDriver, setIsDriver] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingRole, setCheckingRole] = useState(true);

  useEffect(() => {
    let alive = true;

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!alive) return;

      setCheckingRole(true);
      setUser(u);

      if (!u) {
        setIsDriver(false);
        setIsAdmin(false);
        setCheckingRole(false);
        return;
      }

      try {
        const uid = u.uid;

        const [driverSnap, adminSnap] = await Promise.all([
          getDoc(doc(db, "drivers", uid)),
          getDoc(doc(db, "admins", uid)),
        ]);

        if (!alive) return;
        if (auth.currentUser?.uid !== uid) return;

        setIsDriver(driverSnap.exists());
        setIsAdmin(adminSnap.exists());
      } catch (e) {
        console.error("Role check failed:", e);
        if (!alive) return;
        setIsDriver(false);
        setIsAdmin(false);
      } finally {
        if (!alive) return;
        setCheckingRole(false);
      }
    });

    return () => {
      alive = false;
      unsub();
    };
  }, []);

  if (checkingRole) {
    return (
      <div style={{ minHeight: "100vh" }} className="d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Root user={user} isDriver={isDriver} isAdmin={isAdmin} />} />

        <Route path="/student-login" element={<StudentLogin />} />
        <Route path="/staff-login" element={<StaffLogin />} />
        <Route path="/driver-login" element={<DriverLogin />} />
        <Route path="/admin-login" element={<AdminLogin />} />

        <Route
          path="/driver"
          element={
            <RequireDriver user={user} isDriver={isDriver}>
              <DriverDashboard />
            </RequireDriver>
          }
        />

        <Route
          path="/track/:busId"
          element={
            <RequireAuth user={user}>
              <TrackBus />
            </RequireAuth>
          }
        />

        {/* ✅ ADMIN: default = Student List (AdminStudents) */}
        <Route
          path="/admin"
          element={
            <RequireAdmin user={user} isAdmin={isAdmin}>
              <AdminStudents />
            </RequireAdmin>
          }
        />

        {/* ✅ Route editor moved here */}
        <Route
          path="/admin/route-editor"
          element={
            <RequireAdmin user={user} isAdmin={isAdmin}>
              <AdminRouteUpdate />
            </RequireAdmin>
          }
        />

        {/* keep old paths safe */}
        <Route path="/admin/students" element={<Navigate to="/admin" replace />} />
        <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />

        {/* bus detail pages */}
        <Route path="/bus1-details" element={<Bus1 />} />
        <Route path="/bus2-details" element={<Bus2 />} />
        <Route path="/bus3-details" element={<Bus3 />} />
        <Route path="/bus4-details" element={<Bus4 />} />
        <Route path="/bus5-details" element={<Bus5 />} />
        <Route path="/bus6-details" element={<Bus6 />} />
        <Route path="/bus7-details" element={<Bus7 />} />
        <Route path="/bus8-details" element={<Bus8 />} />
        <Route path="/bus9-details" element={<Bus9 />} />
        <Route path="/bus10-details" element={<Bus10 />} />
        <Route path="/bus11-details" element={<Bus11 />} />
        <Route path="/bus12-details" element={<Bus12 />} />
        <Route path="/bus13-details" element={<Bus13 />} />
        <Route path="/bus14-details" element={<Bus14 />} />
        <Route path="/bus15-details" element={<Bus15 />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}